import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import PhoneSearchPanel from '../../vouchers/components/PhoneSearchPanel';
import VoucherCustomerCard from '../../vouchers/components/VoucherCustomerCard';
import { useVoucherAdmin } from '../../vouchers/hooks/useVoucherAdmin';

export default function AdminVoucherRedeemPage() {
  const [phone, setPhone] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const phoneInputRef = useRef(null);
  const { user, hasRole } = useAuth();
  const {
    results,
    resultsMeta,
    loading,
    error,
    busyVoucherId,
    searchByPhone,
    redeemVoucherHours,
    adjustVoucherHours,
    loadVoucherMovements,
  } = useVoucherAdmin(user);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    const frameId = window.requestAnimationFrame(() => {
      phoneInputRef.current?.focus({ preventScroll: true });
      phoneInputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setSelectedCustomerId(null);
    await searchByPhone(phone, { page: 0 });
  }

  const redeemableCustomers = results.filter((customer) => customer.hoursAvailable > 0 && customer.vouchers?.length > 0);
  const activeCustomer = redeemableCustomers.find((customer) => customer.id === selectedCustomerId) || redeemableCustomers[0] || null;

  return (
    <div className="space-y-6">
      <PhoneSearchPanel
        ref={phoneInputRef}
        phone={phone}
        onPhoneChange={setPhone}
        onSubmit={handleSubmit}
        loading={loading}
      />

      {error && <p className="text-red-600 font-semibold">{error}</p>}

      <div className="grid xl:grid-cols-[320px_minmax(0,1fr)] gap-6">
        <div className="rounded-[28px] border border-[var(--app-border)] bg-[var(--app-surface)] p-5 space-y-4 h-fit shadow-[0_20px_50px_rgba(15,23,42,0.05)] backdrop-blur-xl">
          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Personas</p>
            <div className="grid gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500">Orden</label>
                <select
                  value={resultsMeta.sortBy}
                  onChange={(event) => searchByPhone(phone, { sortBy: event.target.value, page: 0 })}
                  className="app-input-compact rounded-2xl"
                >
                  <option value="surname">Apellido</option>
                  <option value="name">Nombre</option>
                  <option value="phone">Teléfono</option>
                </select>
              </div>
            </div>
          </div>
          {redeemableCustomers.length === 0 ? (
            <div className="rounded-[22px] bg-[rgba(245,245,247,0.9)] px-4 py-5 text-sm text-slate-500">
              Introduce un número móvil para cargar únicamente personas con bonos disponibles para canje.
            </div>
          ) : (
            redeemableCustomers.map((customer) => (
              <button
                key={customer.id}
                type="button"
                onClick={() => setSelectedCustomerId(customer.id)}
                className={`w-full text-left rounded-2xl px-4 py-4 border transition ${
                  activeCustomer?.id === customer.id
                    ? 'border-[rgba(0,113,227,0.18)] bg-[rgba(0,113,227,0.08)] shadow-[0_12px_24px_rgba(0,113,227,0.08)]'
                    : 'border-[var(--app-border)] bg-white/80 hover:bg-white'
                }`}
              >
                <p className="font-medium text-slate-950">{customer.fullName}</p>
                <p className="text-sm text-slate-500">{customer.phone}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mt-2">
                  {customer.profileType === 'GUARDIAN_ONLY' && 'Registro tutor'}
                  {customer.profileType === 'VOUCHER_ONLY' && 'Solo bono'}
                  {customer.profileType === 'GUARDIAN_AND_VOUCHER' && 'Tutor + bono'}
                </p>
                <p className="text-sm font-medium text-[var(--app-accent)] mt-2">{customer.hoursAvailable}h disponibles</p>
              </button>
            ))
          )}
          {resultsMeta.totalPages > 1 && (
            <div className="flex items-center justify-between pt-3">
              <button
                type="button"
                disabled={resultsMeta.page === 0}
                onClick={() => searchByPhone(phone, { page: Math.max(resultsMeta.page - 1, 0) })}
                className="rounded-2xl bg-[rgba(245,245,247,0.9)] px-4 py-2 font-medium disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-sm text-slate-500">
                Página {resultsMeta.page + 1} de {resultsMeta.totalPages}
              </span>
              <button
                type="button"
                disabled={resultsMeta.page + 1 >= resultsMeta.totalPages}
                onClick={() => searchByPhone(phone, { page: resultsMeta.page + 1 })}
                className="rounded-2xl bg-[rgba(245,245,247,0.9)] px-4 py-2 font-medium disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {activeCustomer ? (
            <VoucherCustomerCard
              key={activeCustomer.id}
              customer={activeCustomer}
              busyVoucherId={busyVoucherId}
              onIssueVoucher={null}
              onRedeemVoucher={(voucher, hours, customerPhone) => redeemVoucherHours(voucher.id, { hours }, customerPhone)}
              onAdjustVoucher={hasRole('ADMIN') ? (voucher, payload, customerPhone) => adjustVoucherHours(voucher.id, payload, customerPhone) : null}
              onLoadMovements={loadVoucherMovements}
              onRefresh={() => searchByPhone(phone, { page: resultsMeta.page })}
              actor={user}
            />
          ) : (
            <div className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-10 text-center shadow-[0_24px_60px_rgba(15,23,42,0.05)] backdrop-blur-xl">
              <p className="text-5xl mb-4">🔄</p>
              <p className="text-2xl font-medium text-slate-800">Selecciona una persona con bono activo</p>
              <p className="mt-3 text-slate-500 max-w-xl mx-auto">
                Verás aquí solo personas con horas disponibles para canjear.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
